import React, { useEffect, useRef } from 'react';
import { ModuleReCaptchaMessageErrorType } from '@surveycake/logic-engine';
import {
  useEngineInstance,
  useModuleRecaptcha,
  UseModuleSelector
} from '@surveycake/react-logic-engine';
import { Tag } from '@surveycake/rc';
import { Wording } from '@surveycake/wording/survey';
import { googleRecaptchaKey } from 'config/system';
import { useSurveyConfig } from 'app/core/survey-config';
import { useStyles } from './styles';

function resolverMessage(
  errorType: ModuleReCaptchaMessageErrorType,
  wording: Wording
) {
  if (errorType === 'RE_CAPTCHA_REQUIRED') {
    return wording.module.re_captcha.error.required;
  } else if (errorType === 'RE_CAPTCHA_ERROR') {
    return wording.module.re_captcha.error.exception;
  } else if (errorType === 'RE_CAPTCHA_EXPIRED') {
    return wording.module.re_captcha.error.expired;
  }

  return undefined;
}

const errorTypeSelector: UseModuleSelector<
  'reCaptcha',
  ModuleReCaptchaMessageErrorType
> = reCaptcha => reCaptcha.message.error.type;

export default function ReCAPTCHA() {
  const engine = useEngineInstance();
  const errorType = useModuleRecaptcha(errorTypeSelector);
  const { wording } = useSurveyConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const message = resolverMessage(errorType, wording);
  const styles = useStyles();

  useEffect(() => {
    const widgetId = window.grecaptcha.render(containerRef.current!, {
      sitekey: googleRecaptchaKey,
      theme: 'light',
      callback: (token: string) => engine.setReCaptcha({ token }),
      'error-callback': () => engine.setReCaptcha({ error: 'ERROR' }),
      'expired-callback': () => engine.setReCaptcha({ error: 'EXPIRED' })
    });

    return () => {
      window.grecaptcha.reset(widgetId);
    };
  }, [engine]);

  return (
    <>
      {message && (
        <Tag className={styles.error} color="error">
          {message}
        </Tag>
      )}
      <div ref={containerRef} className={styles.reCAPTCHA} />
    </>
  );
}
